/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactNode, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Banner from '@cpg-its/ads.containers.banner';
import Reaptcha from 'reaptcha';
import { Form, Formik, FormikProps } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { useEpiMailSettings, useEpiMailSettingsLoaded } from '../../hooks/useEpiMailSettings';
import { MailDestination, MailFormValidationSchema, MailFormValues } from './mail-model';
import { useApiResponseError } from '../../hooks/useApiError';
import { FormWrapper } from '../form-wrapper';
import { FormField } from '../../../../TCSMApp/global/components/form-element/form-field';
import { Wysiwyg } from '../wysiwyg';
import { SendMail } from '../../ducks/mail-api';
import { replaceAll } from '../../../../TCSMApp/global/helpers/string-helpers';
import { getBasePath } from '../../helpers/routing';
import { EcdRoutes } from '../../constants/routes';
import { EcdStates } from '../../../root/ecd-model';
import { ContentSpacer } from '../../../../TCSMApp/global/components/layout/generic-content-spacer';

interface Props {
    onNavigateBack: () => void;
    destination: MailDestination;
    onNavigateToSendEmail: (clergyId: string | number, destination: MailDestination) => void;
    isClergy?: boolean;
}

function AppMail(props: Props) {    
    const dispatch = useDispatch();
    const history = useHistory();
    const currentlanguage = useSelector<EcdStates, string>(state => state?.globalization?.currentlanguage);    
    const basePath = getBasePath(currentlanguage);

    const { onNavigateBack, destination, onNavigateToSendEmail, isClergy } = props;

    const { itemId } = useParams<any>();

    const [mailSent, setMailSent] = useState(false);
    const [mailSentErrorMessage, setMailSentErrorMessage] = useState<string>('');
    const [mailSentErrorsDetail, setMailSentErrorsDetail] = useState<string[]>(null);
    const [isRecaptchaValid, setIsRecaptchaValid] = useState(false);
    const [recaptchaResponse, setRecaptchaResponse] = useState(null);

    const mailSettingsLoaded = useEpiMailSettingsLoaded();
    const mailSettings = useEpiMailSettings();
    const apiErrors = useApiResponseError();

    const homePath = `/${EcdRoutes.ECDRoot}`;

    const initialValues: MailFormValues = {
        name: '',
        email: '',	
        message: '',
    };

    function OnSendMailSucess(response) {

        if (response?.code === 'ERROR'){
            setMailSentErrorsDetail(response?.errors);
            setMailSentErrorMessage(response?.message);

            return;
        }

        setMailSent(true);
    }

    function OnSubmit(submitValues: MailFormValues) {
        setMailSentErrorsDetail(null);
        setMailSentErrorMessage(null);

        const mailMessageWithName = replaceAll(mailSettings?.emailBodyTemplate, '{name}', submitValues?.name);
        const mailMessageWithEmail = replaceAll(mailMessageWithName, '{email}', submitValues?.email);
        const mailMessageWithMessage = replaceAll(mailMessageWithEmail, '{message}', submitValues?.message);

        dispatch(SendMail({
            name: submitValues?.name,
            email: submitValues?.email,
            distinationGuid: destination?.itemGuid,
            message: mailMessageWithMessage,
            recaptcha: recaptchaResponse,            
        },   
        isClergy,     
         OnSendMailSucess));
    }

    function OnCancel(canelFormProps: FormikProps<MailFormValues>) {
        canelFormProps.resetForm({});
        window.scrollTo(0, 0);
    }

    function OnNavigationHome() {
        history.push({
            pathname: homePath,            
        });
    }

    function NavigationHome() {
        if (!mailSettings.homeButton) return null;

        return (
        <div className='mail-formbuttons'>            
            <button className='btn-primary' type='button' onClick={OnNavigationHome}>{mailSettings.homeButton}</button>
        </div>);
    }

    function NavigationButtons() {
        if (!mailSettings.backButton) return null;

        return (
        <div className='mail-formbuttons'>
            <button className='btn-secondary' type='button' onClick={onNavigateBack}>{mailSettings.backButton}</button>            
        </div>);
    }

    function onRecaptchaVerified(response: string) {
        setRecaptchaResponse(response);
        setIsRecaptchaValid(true);
    }

    function onRecaptchaExpired() {
        setIsRecaptchaValid(false);
    }

    function RenderCaptcha(recaptchaComponent: ReactNode) {
        if (!mailSettings?.recaptchaSiteKey) return null;

        return (
            <div className='form-item'>
                {recaptchaComponent}
            </div>
        );
    }

    useEffect(() => {
		if (itemId && !destination?.email){
            onNavigateToSendEmail(itemId, destination);
        }
	}, []);

    if (!itemId && !destination?.email){
        history.push(`${basePath}/${EcdRoutes.Clergy}`);        
    }

    if (!mailSettingsLoaded || !mailSettings) {
        return <LoadingCircle />;
    }

    if (mailSent) {
        return (<>
            <h3 className='ecd-page-content-title'>
                {mailSettings?.pageTitle}
            </h3>
            <div className='ecd-page-content-section'>
                <Wysiwyg content={mailSettings?.emailSentMessage} />
                <NavigationHome />
            </div>
        </>);
    }    

    function RenderSentErrorMessage(){
        if (mailSentErrorMessage){
            return (
            <ContentSpacer>
                <Banner
                    banner={{
                        type: 'error',
                        label: <>
                            <p>{mailSentErrorMessage}</p>
                            <p><strong>{mailSentErrorsDetail?.join(', ')}</strong></p>
                            </>,
                    }}
                    />

            </ContentSpacer>);
        }
    }

    return (
        <Reaptcha sitekey={mailSettings?.recaptchaSiteKey} onExpire={onRecaptchaExpired} onVerify={onRecaptchaVerified}>            
            {({ renderExplicitly, reset, execute, recaptchaComponent }) => {
                return (
                <>
                    <h3 className='ecd-page-content-title'>
                        {mailSettings?.pageTitle}
                    </h3>
                    <div className='ecd-page-content-section'>
                        {RenderSentErrorMessage()}
                        <NavigationButtons />
                        <Wysiwyg content={mailSettings?.formInstructionMessage} />
                        <Formik
                            initialValues={initialValues}
                            onSubmit={OnSubmit}
                            validationSchema={MailFormValidationSchema(mailSettings)}
                            enableReinitialize
                            initialErrors={apiErrors}
                        >
                            {(formProps: FormikProps<MailFormValues>) => (
                                <Form>
                                    <FormWrapper
                                        formikProps={formProps}
                                        submitDisabled={!isRecaptchaValid}
                                        submitText={mailSettings?.submitFormButton}
                                        cancelText={mailSettings?.clearFormButton}
                                        onCancel={() => { OnCancel(formProps); }}
                                    >
                                        <div className='form-group'>
                                            <FormField
                                                name='name'
                                                label={mailSettings?.nameLabel || 'Your Name'}
                                                component='input'
                                                touched={formProps.touched}
                                                errors={formProps.errors}
                                                required
                                            />
                                            <FormField
                                                name='email'
                                                label={mailSettings?.emailLabel || 'Your e-mail address'}
                                                component='input'
                                                touched={formProps.touched}
                                                errors={formProps.errors}
                                                required
                                            />
                                            <p><strong>{mailSettings?.toLabel}</strong>: {destination?.name} </p>
                                            <FormField
                                                name='message'
                                                label={mailSettings?.messageLabel || 'Your Message'}
                                                component='input'
                                                multiline
                                                rows={5}
                                                touched={formProps.touched}
                                                errors={formProps.errors}
                                                required
                                            />                                                                                
                                            { RenderCaptcha(recaptchaComponent) }                                            
                                        </div>
                                    </FormWrapper>
                                </Form>
                            )}
                        </Formik >                        
                    </div>
                </>                
                );
            }}
        </Reaptcha>        
    );
}

export default AppMail;
AppMail.defaultProps = {
    isClergy: false,
};
